import { RefObject, useEffect, useMemo, useState } from "react";

import { FormInstance } from "antd";

import { generateRandomId } from "utils";

/**
 * A list entry paired with a client-only key that stays stable for the entry's lifetime in the form.
 *
 * @template T The entry shape the API works with (e.g. `UploadableFeedAdditive`).
 */
export type KeyedEntry<T> = T & {
  /** Client-only identifier; never sent to the API. Use it for the entry's React `key` and in its form field `name`s. */
  clientKey: string;
};

/** Options for {@link useKeyedFormList}. */
export interface UseKeyedFormListOptions {
  /**
   * True once the form and everything the entries' fields depend on are mounted (typically `!isLoading`, plus any condition that gates rendering
   * the form). A requested rehydration validation waits until this is true.
   */
  isReady: boolean;
}

/** The operations on a {@link KeyedFormList}. The object and every function on it keep the same identity for the component's lifetime. */
export interface KeyedFormListActions<T extends object> {
  /**
   * Replaces the whole list, e.g. with data loaded from the API. Every item gets a fresh `clientKey`, so its fields mount fresh from their
   * `initialValue` and any previous errors are cleared.
   */
  hydrate: (items: T[]) => void;
  /** Appends one item with a new `clientKey`. Its fields mount without errors and stay neutral until touched. */
  add: (item: T) => void;
  /** Merges `changes` into the entry with the given `clientKey`; other entries are left untouched. */
  update: (clientKey: string, changes: Partial<T>) => void;
  /** Removes the entry with the given `clientKey`. Later entries keep their keys, so their values and errors move up with them. */
  remove: (clientKey: string) => void;
  /**
   * Asks for one `validateFields()` call on the form once `isReady` is true and the list has entries, so errors show on rehydrated data even
   * when the request is made before the entries' fields have mounted. Call it from the effect that loads saved data, only when that data
   * should be checked (e.g. the user answered the question on a previous visit).
   */
  requestRehydrationValidation: () => void;
}

/** The list state and operations returned by {@link useKeyedFormList}. */
export interface KeyedFormList<T extends object> {
  /** The current entries, in display order, each carrying its `clientKey`. */
  entries: KeyedEntry<T>[];
  /** The current entries with `clientKey` removed, ready to go into a request payload. */
  payload: Omit<KeyedEntry<T>, "clientKey">[];
  /**
   * The list operations, grouped so effects can depend on `list.actions` (stable) rather than on the list itself, which changes whenever the
   * entries do. Depending on the list from a loading effect would re-run it, re-hydrate, and loop.
   */
  actions: KeyedFormListActions<T>;
}

const withClientKey = <T extends object>(item: T): KeyedEntry<T> => ({ ...item, clientKey: generateRandomId() });

/**
 * Holds a dynamic list of form entries (add/remove rows) where each entry has a stable `clientKey`.
 *
 * Keying entries by `clientKey` instead of their index means that when an earlier entry is removed, the later entries keep their React
 * components and form fields, so their values and validation errors move up with them. It also makes the old "call `resetFields()` whenever
 * the list length changes" workaround unnecessary; that workaround wiped every error on screen.
 *
 * Validation rule this hook is built around: entries loaded from saved data can be validated (via `requestRehydrationValidation`) so
 * incomplete ones show errors immediately; entries the user creates (via `add`, or a yes/no question switching the list on) stay neutral
 * until touched.
 *
 * Pair it with `preserve={false}` on the `<Form>` so removed entries' fields leave the form store.
 *
 * @template T The entry shape the API works with.
 * @param formRef Ref to the antd form that renders the entries' fields; used only for the rehydration validation.
 * @param options See {@link UseKeyedFormListOptions}.
 * @returns See {@link KeyedFormList}.
 *
 * @example
 * const keyedAdditives = useKeyedFormList<UploadableFeedAdditive>(formRef, { isReady: !isLoading });
 *
 * useEffect(() => {
 *   if (savedAdditives) keyedAdditives.actions.hydrate(savedAdditives.length ? savedAdditives : [additivePlaceholder]);
 * }, [savedAdditives, keyedAdditives.actions]);
 *
 * keyedAdditives.entries.map((additive, index) => (
 *   <Flex key={additive.clientKey}>
 *     <InputNumber
 *       name={`amount-${additive.clientKey}`}
 *       dataTestId={`amount-input-${index}`}
 *       onChange={(amount) => keyedAdditives.actions.update(additive.clientKey, { amount })}
 *     />
 *     <IconButton onClick={() => keyedAdditives.actions.remove(additive.clientKey)} />
 *   </Flex>
 * ));
 *
 * // In the mutation payload
 * selectedFeedAdditive: keyedAdditives.payload.filter((additive) => !!additive.feedAdditiveId)
 */
export const useKeyedFormList = <T extends object>(formRef: RefObject<FormInstance | null>, { isReady }: UseKeyedFormListOptions): KeyedFormList<T> => {
  const [entries, setEntries] = useState<KeyedEntry<T>[]>([]);
  const [isAwaitingRehydrationValidation, setIsAwaitingRehydrationValidation] = useState(false);

  // Runs after the commit that mounts the entries' fields, which a validateFields() call in the loading effect itself would be too early for
  useEffect(() => {
    if (isAwaitingRehydrationValidation && isReady && entries.length > 0) {
      formRef.current?.validateFields();
      setIsAwaitingRehydrationValidation(false);
    }
  }, [isAwaitingRehydrationValidation, isReady, entries.length, formRef]);

  // Only state setters are used, so the actions never need to change identity
  const actions = useMemo<KeyedFormListActions<T>>(
    () => ({
      hydrate: (items) => setEntries(items.map(withClientKey)),
      add: (item) => setEntries((current) => [...current, withClientKey(item)]),
      update: (clientKey, changes) => setEntries((current) => current.map((entry) => (entry.clientKey === clientKey ? { ...entry, ...changes } : entry))),
      remove: (clientKey) => setEntries((current) => current.filter((entry) => entry.clientKey !== clientKey)),
      requestRehydrationValidation: () => setIsAwaitingRehydrationValidation(true),
    }),
    []
  );

  const payload = useMemo(() => entries.map(({ clientKey: _clientKey, ...entry }) => entry), [entries]);

  return { entries, payload, actions };
};
